import type { HelpGuideId, HelpLocation } from '../../content/types';

export type HelpAssistantPanelMode = 'help' | 'assistant';

export const DEFAULT_PANEL_WIDTH = 480;
export const MIN_PANEL_WIDTH = 320;
export const MAX_PANEL_WIDTH = 960;

export interface HelpAssistantPanelState {
    isOpen: boolean;
    activeMode: HelpAssistantPanelMode;
    helpLocation: HelpLocation;
    hoveredStepIndex: number | null;
    guideCompact: boolean;
    /**
     * Whether the user has opened the panel at least once in this model
     * lifetime. Used to distinguish a true virgin state from a state that
     * merely *looks* virgin after a closed root-Help session, so RESUME_PANEL
     * can decide whether to fall back to the Ask tab.
     */
    hasOpenedOnce: boolean;
    panelWidth: number;
}

export interface HelpAssistantPanelStateInit {
    isOpen?: boolean;
    activeMode?: HelpAssistantPanelMode;
    helpLocation?: HelpLocation;
    hasOpenedOnce?: boolean;
    panelWidth?: number;
}

export const createDefaultHelpLocation = (): HelpLocation => ({ type: 'browse', path: [] });

export function clampPanelWidth(width: number): number {
    if (!Number.isFinite(width)) return DEFAULT_PANEL_WIDTH;
    return Math.min(MAX_PANEL_WIDTH, Math.max(MIN_PANEL_WIDTH, Math.round(width)));
}

export const createInitialHelpAssistantPanelState = ({
    isOpen = false,
    activeMode = 'help',
    helpLocation = createDefaultHelpLocation(),
    hasOpenedOnce = false,
    panelWidth = DEFAULT_PANEL_WIDTH,
}: HelpAssistantPanelStateInit = {}): HelpAssistantPanelState => ({
    isOpen,
    activeMode,
    helpLocation,
    hoveredStepIndex: null,
    guideCompact: false,
    hasOpenedOnce,
    panelWidth: clampPanelWidth(panelWidth),
});

export function areHelpLocationsEqual(a: HelpLocation, b: HelpLocation): boolean {
    if (a.type !== b.type) return false;

    switch (a.type) {
        case 'browse':
            return (
                b.type === 'browse' && a.path.length === b.path.length && a.path.every((part, i) => part === b.path[i])
            );
        case 'document':
            return b.type === 'document' && a.documentId === b.documentId;
        case 'guide':
            return b.type === 'guide' && a.guideId === b.guideId && a.stepIndex === b.stepIndex;
        case 'contact':
            return b.type === 'contact';
        default: {
            const _exhaustiveCheck: never = a;
            return _exhaustiveCheck;
        }
    }
}

export function isGuideLocationWithStep(
    location: HelpLocation,
    guideId: HelpGuideId,
    stepIndex: number,
): location is Extract<HelpLocation, { type: 'guide' }> {
    return location.type === 'guide' && location.guideId === guideId && location.stepIndex === stepIndex;
}
